/**
 * Book-builder funnel events, pushed to Google Tag Manager (GTM-PZ6483CS).
 *
 * Tracking only. GTM routes each event to GA4 and Meta; the Meta tags skip
 * `channel: 'etsy'` because Etsy buyers have already paid and would skew ad
 * optimisation. Nothing here may throw or block the flow that calls it.
 *
 * Each (event, book) pair is sent at most once per browser: the user can go
 * back and continue/approve again, and that must not count as a second
 * drop-off step. The record lives in localStorage, so a logout (which clears
 * it) can let a repeat through — acceptable for funnel counts.
 *
 * Never put names, emails, titles or photos in the payload.
 */
import { isEtsyUser } from '../services/etsyService';
import { sanitizeBookTag } from './bookTag';

export const FUNNEL_EVENTS = Object.freeze({
  START_BOOK: 'start_book',
  PHOTOS_UPLOADED: 'photos_uploaded',
  BOOK_APPROVED: 'book_approved',
});

const SENT_KEY = 'md_funnel_events_sent';
const MAX_REMEMBERED = 300;

function readSent() {
  try {
    const parsed = JSON.parse(localStorage.getItem(SENT_KEY));
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    return [];
  }
}

function rememberSent(key) {
  try {
    const sent = readSent().filter((k) => k !== key);
    sent.push(key);
    localStorage.setItem(SENT_KEY, JSON.stringify(sent.slice(-MAX_REMEMBERED)));
  } catch (err) {
    /* storage full or blocked — worst case the event can repeat */
  }
}

/**
 * @param {string} event one of FUNNEL_EVENTS
 * @param {object} opts
 * @param {string} opts.bookId
 * @param {object} [opts.profile] state.profile.data — decides web vs etsy
 * @param {string} [opts.tag] book purchase-format tag (see util/bookTag)
 * @param {object} [opts.extras] extra non-personal params, e.g. { photo_count }
 * @returns {boolean} true when the event was pushed
 */
export function pushFunnelEvent(event, { bookId, profile, tag, extras } = {}) {
  try {
    if (!event || !bookId) return false;
    const key = `${event}:${bookId}`;
    if (readSent().includes(key)) return false;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      book_id: String(bookId),
      book_format: sanitizeBookTag(tag) === 'downloadPdf' ? 'pdf' : 'print',
      channel: isEtsyUser(profile) ? 'etsy' : 'web',
      ...extras,
    });
    rememberSent(key);
    return true;
  } catch (err) {
    console.warn('[GTM] funnel event failed (non-blocking):', event, err);
    return false;
  }
}
